import { mixins } from "../app/mixin";
import useVuelidate from "@vuelidate/core";
import { required, email, sameAs, minLength, maxLength } from "@vuelidate/validators";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/js/utils.js";

import countriesLib from "i18n-iso-countries";
import enCountries from "i18n-iso-countries/langs/en.json";
import trCountries from "i18n-iso-countries/langs/tr.json";
countriesLib.registerLocale(enCountries);
countriesLib.registerLocale(trCountries);

// digits-only helpers
const MAX_NATIONAL_LEN = 15;
const sanitizeDigits = (val) => String(val || "").replace(/\D/g, "").slice(0, MAX_NATIONAL_LEN);

// normalize for search (remove diacritics, locale-case)
function normalizeForSearch(str, lang) {
  return String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase(lang === "tr" ? "tr" : "en");
}

function buildCountryOptions(lang) {
  const isoLang = lang === 'tr' ? 'tr' : 'en';
  // Some versions support the second arg; if not, it's ignored harmlessly
  const names = countriesLib.getNames(isoLang, { select: 'official' });
  const list = Object.entries(names).map(([code, label]) => ({
    value: code, label
  }));
  // Alphabetical, locale-aware
  list.sort((a, b) => a.label.localeCompare(b.label, isoLang));
  return list;
}

const validateWorkEmail = (value) => {
  if (!value) return false;
  const publicDomains = [
    "gmail.com","yahoo.com","outlook.com","hotmail.com","aol.com","icloud.com",
    "mail.com","yandex.com","protonmail.com","zoho.com","gmx.com"
  ];
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(value)) return false;
  const domain = String(value).split("@")[1];
  if (publicDomains.includes(domain)) return false;
  const workEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return workEmailPattern.test(value);
};

mixins.push({
  el: "#contactForm",
  delimiters: ["${", "}"],
  
  validations: {
    companiesForm: {
      firstName: { required, minLength: minLength(2) },
      lastName:  { required },
      email:     { required, email, validateWorkEmail },
      phone:     { required },
      message:   { required, maxLength: maxLength(1024) },
      isCheckboxChecked: { required, sameAs: sameAs(true) },
    },
    partnersForm: {
      firstName:   { required, minLength: minLength(2) },
      lastName:    { required },
      email:       { required, email, validateWorkEmail },
      companyName: { required },
      industry:    { required },
      country:     { required },
      companySize: { required },
      message:     { maxLength: maxLength(1024) },
      isCheckboxChecked: { required, sameAs: sameAs(true) },
    },
  },
  
  data() {
    return {
      v: useVuelidate(),
      api: window.api,
      lang: window.lang,
      siteKey: window.siteKey,
      errorMessages: window.errorMessages,
      
      // UI
      activeTab: "work",
      isSubmittingCompanies: false,
      isSubmittingPartners: false,
      showSuccessMessage: false,
      
      // legacy flags (to silence old template refs if any)
      showFormErrorMessage: false,
      showSubmitErrorMessage: false,
      formErrorTimerId: null,
      submitErrorTimerId: null,
      
      // dropdown state & labels (partners form)
      isIndustryOpen: false,
      isCompanySizeOpen: false,
      isCountryOpen: false,
      industryLabel: "",
      companySizeLabel: "",
      countryLabel: "",
      activeDropdown: null,
      closeDropdownHandler: null,
      
      // options
      industryOptions: [],
      companySizeOptions: [],
      countryOptions: [],
      
      // SEARCH state for country dropdown
      countrySearch: '',
      
      // FORMS
      companiesForm: {
        firstName: "",
        lastName:  "",
        email:     "",
        phone:     "",
        message:   "",
        isCheckboxChecked: false,
      },
      partnersForm: {
        firstName:   "",
        lastName:    "",
        email:       "",
        companyName: "",
        industry:    "",
        country:     "",
        companySize: "",
        message:     "",
        isCheckboxChecked: false,
      },
    };
  },
  
  created() {
    // Industry
    this.industryOptions = this.lang === "en"
      ? [
        { value: "finance",        label: "Finance" },
        { value: "manufacturing",  label: "Manufacturing" },
        { value: "retail",         label: "Retail" },
        { value: "tourism",        label: "Tourism" },
        { value: "technology",     label: "Technology" },
        { value: "energy",         label: "Energy" },
        { value: "transportation", label: "Transportation" },
        { value: "other",          label: "Other" },
      ]
      : [
        { value: "energy",         label: "Enerji" },
        { value: "finance",        label: "Finans" },
        { value: "transportation", label: "Lojistik" },
        { value: "retail",         label: "Perakende" },
        { value: "technology",     label: "Teknoloji" },
        { value: "tourism",        label: "Turizm" },
        { value: "manufacturing",  label: "İmalat" },
        { value: "other",          label: "Diğer" },
      ];
    
    // Company size
    this.companySizeOptions = [
      { value: "0-10",      label: "0–10" },
      { value: "11-50",     label: "11–50" },
      { value: "51-250",    label: "51–250" },
      { value: "251-1000",  label: "251–1000" },
      { value: "1000+",     label: "1000+" },
    ];
    
    // Country list (TR/EN)
    this.countryOptions = buildCountryOptions(this.lang);
  },
  
  mounted() {
    this.setInitialTabFromHash();
    
    const phoneInput = document.querySelector("#c_phone");
    if (phoneInput) {
      this.iti = intlTelInput(phoneInput, {
        separateDialCode: true,
        initialCountry: "tr",
      });
      
      const applySanitize = () => {
        const sanitized = sanitizeDigits(phoneInput.value);
        if (phoneInput.value !== sanitized) {
          phoneInput.value = sanitized;
        }
        if (this.companiesForm.phone !== sanitized) {
          this.companiesForm.phone = sanitized;
        }
        this.v.companiesForm.phone.$touch();
      };
      
      phoneInput.addEventListener("input", applySanitize);
      
      // When user copied the phone number (max 15 chars)
      phoneInput.addEventListener("paste", (e) => {
        e.preventDefault();
        const clip = (e.clipboardData || window.clipboardData)?.getData("text") || "";
        const merged = (phoneInput.value || "") + clip;
        const sanitized = sanitizeDigits(merged);
        phoneInput.value = sanitized;
        this.companiesForm.phone = sanitized;
        this.v.companiesForm.phone.$touch();
      });
    }
  },
  
  computed: {
    // localized placeholder for country search
    countrySearchPlaceholder() {
      return this.lang === 'tr' ? 'Ülke ara...' : 'Search country...';
    },
    
    // filtered list by search term
    filteredCountryOptions() {
      const term = normalizeForSearch(this.countrySearch, this.lang);
      if (!term) return this.countryOptions;
      return this.countryOptions.filter(opt =>
        normalizeForSearch(opt.label, this.lang).includes(term)
      );
    },
    
    // Companies (work) error messages
    errorCompanies() {
      let c_firstName, c_lastName, c_email, c_phone, c_message, c_checkbox;
      
      if (this.v.companiesForm.firstName.$error) c_firstName = this.errorMessages.required;
      if (this.v.companiesForm.lastName.$error)  c_lastName  = this.errorMessages.required;
      
      if (this.v.companiesForm.email.$error) {
        if (this.v.companiesForm.email.required && this.companiesForm.email.length === 0) {
          c_email = this.errorMessages.required;
        } else if (this.v.companiesForm.email.email.$invalid) {
          c_email = this.errorMessages.emailIsInvalid;
        } else if (this.v.companiesForm.email.validateWorkEmail?.$invalid) {
          c_email = this.errorMessages.mustBeWorkEmail;
        }
      }
      
      if (this.v.companiesForm.phone.$error) {
        c_phone = this.errorMessages.required;
      }
      
      if (this.v.companiesForm.message.$error) {
        c_message = this.v.companiesForm.message.maxLength?.$invalid
          ? this.errorMessages.maxLength1024
          : this.errorMessages.required;
      }
      
      if (this.v.companiesForm.isCheckboxChecked.$error) c_checkbox = this.errorMessages.required;
      
      return { c_firstName, c_lastName, c_email, c_phone, c_message, c_checkbox };
    },
    
    // Partners error messages
    errorPartners() {
      let p_firstName, p_lastName, p_email,
        p_companyName, p_industry, p_country, p_companySize,
        p_message, p_checkbox;
      
      if (this.v.partnersForm.firstName.$error)   p_firstName   = this.errorMessages.required;
      if (this.v.partnersForm.lastName.$error)    p_lastName    = this.errorMessages.required;
      
      if (this.v.partnersForm.email.$error) {
        if (this.v.partnersForm.email.required && this.partnersForm.email.length === 0) {
          p_email = this.errorMessages.required;
        } else if (this.v.partnersForm.email.email.$invalid) {
          p_email = this.errorMessages.emailIsInvalid;
        } else if (this.v.partnersForm.email.validateWorkEmail?.$invalid) {
          p_email = this.errorMessages.mustBeWorkEmail;
        }
      }
      
      if (this.v.partnersForm.companyName.$error) p_companyName = this.errorMessages.required;
      if (this.v.partnersForm.industry.$error)    p_industry    = this.errorMessages.required;
      if (this.v.partnersForm.country.$error)     p_country     = this.errorMessages.required;
      if (this.v.partnersForm.companySize.$error) p_companySize = this.errorMessages.required;
      
      if (this.v.partnersForm.message?.maxLength?.$invalid) {
        p_message = this.errorMessages.maxLength1024;
      }
      
      if (this.v.partnersForm.isCheckboxChecked.$error) p_checkbox = this.errorMessages.required;
      
      return {
        p_firstName, p_lastName, p_email,
        p_companyName, p_industry, p_country, p_companySize,
        p_message, p_checkbox
      };
    }
  },
  
  methods: {
    setInitialTabFromHash() {
      const hash = window.location.hash;
      if (!hash) return;
      
      // If #partner-form
      if (hash === '#partner-form') {
        this.activeTab = 'partner';
      }
      
      const section = document.querySelector('.main-content')
      const headerOffset = 150;
      const y = section.getBoundingClientRect().top - headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    },
    
    switchTab(tab) {
      this.activeTab = tab;
      this.v.$reset();
      // close any open dropdown
      this.isIndustryOpen = this.isCompanySizeOpen = this.isCountryOpen = false;
      this.activeDropdown = null;
      if (this.closeDropdownHandler) {
        document.removeEventListener("click", this.closeDropdownHandler);
        this.closeDropdownHandler = null;
      }
    },
    
    showFormError() {
      if (this.formErrorTimerId) clearTimeout(this.formErrorTimerId);
      this.showFormErrorMessage = true;
      this.formErrorTimerId = setTimeout(() => {
        this.showFormErrorMessage = false;
        this.formErrorTimerId = null;
      }, 5000);
    },
    showSubmitError() {
      if (this.submitErrorTimerId) clearTimeout(this.submitErrorTimerId);
      this.showSubmitErrorMessage = true;
      this.submitErrorTimerId = setTimeout(() => {
        this.showSubmitErrorMessage = false;
        this.submitErrorTimerId = null;
      }, 5000);
    },
    closeSuccessModal() {
      this.showSuccessMessage = false;
    },
    closeFormErrorModal() {
      if (this.formErrorTimerId) clearTimeout(this.formErrorTimerId);
      this.showFormErrorMessage = false;
      this.formErrorTimerId = null;
    },
    closeSubmitErrorModal() {
      if (this.submitErrorTimerId) clearTimeout(this.submitErrorTimerId);
      this.showSubmitErrorMessage = false;
      this.submitErrorTimerId = null;
    },
    
    onMessageInput(formKey) {
      this.v[formKey].message.$touch();
    },
    
    // Custom select helpers
    toggleDropdown(isOpenKey, formKey, fieldKey, event) {
      event?.stopPropagation?.();
      
      if (this.activeDropdown && this.activeDropdown !== isOpenKey) {
        this[this.activeDropdown] = false;
      }
      
      this[isOpenKey] = !this[isOpenKey];
      this.activeDropdown = this[isOpenKey] ? isOpenKey : null;
      
      if (this[isOpenKey]) {
        this.closeDropdownHandler = (e) => this.closeDropdownOnOutsideClick(isOpenKey, formKey, fieldKey, e);
        setTimeout(() => document.addEventListener("click", this.closeDropdownHandler), 0);
      } else {
        document.removeEventListener("click", this.closeDropdownHandler);
        this.closeDropdownHandler = null;
        this.v[formKey][fieldKey].$touch();
      }
    },
    
    closeDropdownOnOutsideClick(isOpenKey, formKey, fieldKey, event) {
      const selectBox = event.target.closest(".custom-select");
      if (selectBox && this.$el.contains(selectBox)) return;
      
      this[isOpenKey] = false;
      this.activeDropdown = null;
      this.v[formKey][fieldKey].$touch();
      document.removeEventListener("click", this.closeDropdownHandler);
      this.closeDropdownHandler = null;
    },
    
    selectOption(formKey, fieldKey, labelKey, isOpenKey, option) {
      this[formKey][fieldKey] = option.value;
      this[labelKey] = option.label;
      this[isOpenKey] = false;
      this.activeDropdown = null;
      this.v[formKey][fieldKey].$touch();
      document.removeEventListener("click", this.closeDropdownHandler);
      this.closeDropdownHandler = null;
    },
    
    // Robust scroll to first error inside a given panel
    scrollToFirstErrorInPanel(panelSelector) {
      const root = document.querySelector(panelSelector);
      if (!root) return;
      
      const headerOffset = 150; // adjust if needed
      const findFirst = () =>
        root.querySelector(".input-group.error, .checkbox-group.error, [aria-invalid='true']");
      
      const focusAndScroll = (el) => {
        const focusable = el.querySelector("input, textarea, select, [tabindex]");
        if (focusable) {
          try { focusable.focus({ preventScroll: true }); } catch (_) {}
        }
        const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      };
      
      // Wait until DOM paints the error classes
      this.$nextTick(() => {
        let tries = 0;
        const tick = () => {
          const firstError = findFirst();
          if (firstError) {
            focusAndScroll(firstError);
          } else if (tries++ < 12) {
            requestAnimationFrame(tick);
          }
        };
        requestAnimationFrame(tick);
      });
    },
    
    // Submit — companies (Work tab form)
    async submitCompaniesForm() {
      this.v.companiesForm.$touch();
      if (this.v.companiesForm.$invalid) {
        this.showFormError();
        this.scrollToFirstErrorInPanel("#panel-work");
        return;
      }
      
      this.companiesForm.phone = sanitizeDigits(this.companiesForm.phone);
      
      this.isSubmittingCompanies = true;
      
      try {
        // E.164 if possible; fallback +dialCode national
        const dial = this.iti?.getSelectedCountryData()?.dialCode || "";
        const e164 = this.iti?.getNumber ? this.iti.getNumber() : null;
        const phoneForSubmit = e164 || (dial ? `+${dial} ${this.companiesForm.phone}` : this.companiesForm.phone);
        
        const token = await grecaptcha.execute(this.siteKey, { action: "contact_submit" });
        const res = await fetch(`${this.api}/contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept-Language": this.lang },
          body: JSON.stringify({
            formType:  "work_with_us",
            firstName: this.companiesForm.firstName,
            lastName:  this.companiesForm.lastName,
            email:     this.companiesForm.email,
            phone:     phoneForSubmit,
            message:   this.companiesForm.message,
            recaptchaToken: token,
          }),
        });
        
        if (res.status === 201) {
          this.showSuccessMessage = true;
          this.resetForms();
        } else if (res.status === 422) {
          this.showSubmitError();
        } else {
          throw new Error("submit_failed");
        }
      } catch (e) {
        this.showSubmitError();
      } finally {
        this.isSubmittingCompanies = false;
      }
    },
    
    // Submit — partners
    async submitPartnersForm() {
      this.v.partnersForm.$touch();
      if (this.v.partnersForm.$invalid) {
        this.showFormError();
        this.scrollToFirstErrorInPanel("#panel-partner");
        return;
      }
      
      this.isSubmittingPartners = true;
      
      try {
        const token = await grecaptcha.execute(this.siteKey, { action: "contact_submit" });
        const res = await fetch(`${this.api}/contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept-Language": this.lang },
          body: JSON.stringify({
            formType:    "partner_with_us",
            firstName:   this.partnersForm.firstName,
            lastName:    this.partnersForm.lastName,
            email:       this.partnersForm.email,
            companyName: this.partnersForm.companyName,
            industry:    this.partnersForm.industry,
            country:     this.partnersForm.country,
            companySize: this.partnersForm.companySize,
            message:     this.partnersForm.message || null,
            recaptchaToken: token,
          }),
        });
        
        if (res.status === 201) {
          this.showSuccessMessage = true;
          this.resetForms();
        } else if (res.status === 422) {
          this.showSubmitError();
        } else {
          throw new Error("submit_failed");
        }
      } catch (e) {
        this.showSubmitError();
      } finally {
        this.isSubmittingPartners = false;
      }
    },
    
    resetForms() {
      this.companiesForm = {
        firstName: "", lastName: "", email: "", phone: "", message: "", isCheckboxChecked: false
      };
      this.partnersForm = {
        firstName: "", lastName: "", email: "", companyName: "",
        industry: "", country: "", companySize: "", message: "",
        isCheckboxChecked: false
      };
      
      this.industryLabel = "";
      this.companySizeLabel = "";
      this.countryLabel = "";
      
      this.v.$reset();
      
      setTimeout(() => { this.showSuccessMessage = false; }, 6000);
    },
  },
});
